import { OpponentLineupVisibility, PublicLineupVisibility, TieBreakerLineupVisibility } from '@/entities/season-division/SeasonDivisionPool';
import { TeamLeagueType } from '@/lib/types';
import { TimezoneLegacy } from '@/utils/types';

export interface TeamLeagueInfo {
	uuid: string;
	seasonUuid: string;
	logo: Logo;
	title: string;
	countryId: number;
	localizationId: number;
	organizationUuid: string;
	organizationTitle: string;
	facebook: string;
	twitter: string;
	instagram: string;
	snapchat: string;
	youtube: string;
	tiktok: string;
	dateStart: Date | null;
	dateEnd: Date | null;
	registrationDateOpen: Date | null;
	registrationDateClose: Date | null;
	maxTeamsCount: number;
	registeredTeamsCount: number;
	currencyId: number;
	useScheduleGroups: boolean;
	useOnlineRegistration: boolean;
	isSeasonActive: boolean;
	isPartOfTeamLeague: boolean | null;
	ratingOption: 'RATING_DUPR' | 'RATING_WPR' | 'RATING_UNDEFINED';
	timezoneId: number;
	timezone: TimezoneLegacy;
	autoApproveTransfers: ApproveTransfers;
	hideAllRegistrationInfo: boolean;
	utcOffset?: number;
	tieBreakerMatchTitle: string;
	tieBrekaerMatchPluralTitle: string;
	districtSingularTitle: string;
	districtPluralTitle: string;
	websiteUrl: string | null;
	ticketsUrl: string | null;
	matchupSingularTitle: string;
	matchupPluralTitle: string;
	showToPublic: boolean;
	showPlayerRatingsDoubles: boolean;
	showPlayerRatingsSingles: boolean;
	showPlayerRatingsSkinnySingles: boolean;
	opponentLineupVisibility: OpponentLineupVisibility;
	publicLineupVisibility: PublicLineupVisibility;
	tieBreakerLineupOption: TieBreakerLineupVisibility;
	seasonTitle: string | null;
	hideSeasonStartDate: boolean;
	hideTlLocation: boolean;
	useLineupStrength: boolean;
	usePodValidation: boolean;
	hideTeamRatings: boolean;
	seasonStandingsExist: boolean;
	tieBreakerMatchAbbreviation: string;
	isMemberOfMlp?: boolean;
	keywords?: string;
	nextSeason?: {
		uuid: string;
		title: string;
		dateStart: string;
		dateEnd: string;
		slug: string;
		showToPublic: boolean;
		showToPublicDateTime: string;
		showOnSearch: boolean;
		isActive: boolean;
		isExhibition: boolean;
		registrationStartDate: string | null;
		registrationEndDate: string | null;
		useOnlineRegistration: boolean;
	} | null;

	// Standings table column visibility
	standingsTableStandings: boolean;
	standingsTableMatchupsPlayed: boolean;
	standingsTableMatchupWins: boolean;
	standingsTableMatchupWinsRegulation: boolean;
	standingsTableMatchupWinsTiebreaker: boolean;
	standingsTableMatchupWinsTotal: boolean;
	standingsTableMatchupLost: boolean;
	standingsTableMatchesTied: boolean;
	standingsTableMatchupsTied: boolean;
	standingsTablePointsPerMatchup: boolean;
	standingsTableLastFiveMatchups: boolean;
	standingsTableMatches: boolean;
	standingsTableMatchesWon: boolean;
	standingsTableMatchesLost: boolean;
	standingsTableMatchWinRatio: boolean;
	standingsTableGames: boolean;
	standingsTableGamesWon: boolean;
	standingsTableGamesLost: boolean;
	standingsTableGameWinRatio: boolean;
	standingsTableGamePoints: boolean;
	standingsTableGamePointsPe: boolean;
	standingsTableGamePointsPa: boolean;
	standingsTableGamePointsPd: boolean;

	// Players table column visibility
	playerTableMatches: boolean;
	playerTableMatchesWon: boolean;
	playerTableMatchesLost: boolean;
	playerTableMatchWinRatio: boolean;
	playerTableGames: boolean;
	playerTableGamesWon: boolean;
	playerTableGamesLost: boolean;
	playerTableGamesDifferential: boolean;
	playerTablePoints: boolean;
	playerTablePointsEarned: boolean;
	playerTablePointsAgainst: boolean;
	playerTablePointsDifferential: boolean;
	playerTableTiebreakerPoints: boolean;
	playerTableTiebreakerPointsEarned: boolean;
	playerTableTiebreakerPointsAgainst: boolean;
	playerTableTiebreakerPointsDifferential: boolean;
	playerTableRatings: boolean;
	playerTableAveragePartnerRating: boolean;
	playerTableAverageOpponentRating: boolean;
	playerTableAdjustedOpponentRating: boolean;
	playerTableOverallRatings: boolean;
	playerTableLastFiveMatches: boolean;

	// Team Leaderboard table column visibility
	teamLeaderboardStandingPoints: boolean;
	teamLeaderboardMatchupsPlayed: boolean;
	teamLeaderboardMatchupWins: boolean;
	teamLeaderboardMatchupWinsRegulation: boolean;
	teamLeaderboardMatchupWinsTiebreaker: boolean;
	teamLeaderboardMatchupWinsTotal: boolean;
	teamLeaderboardMatchupsLost: boolean;
	teamLeaderboardPointsPerMatchup: boolean;
	teamLeaderboardLastFiveMatchups: boolean;
	teamLeaderboardMatches: boolean;
	teamLeaderboardMatchesWon: boolean;
	teamLeaderboardMatchesLost: boolean;
	teamLeaderboardMatchWinRatio: boolean;
	teamLeaderboardGames: boolean;
	teamLeaderboardGamesWon: boolean;
	teamLeaderboardGamesLost: boolean;
	teamLeaderboardGameWinRatio: boolean;
	teamLeaderboardGamePoints: boolean;
	teamLeaderboardGamePointsPe: boolean;
	teamLeaderboardGamePointsPa: boolean;
	teamLeaderboardGamePointsPd: boolean;

	// Player Leaderboard table column visibility
	playerLeaderboardMatches: boolean;
	playerLeaderboardMatchesWon: boolean;
	playerLeaderboardMatchesLost: boolean;
	playerLeaderboardMatchWinRatio: boolean;
	playerLeaderboardGames: boolean;
	playerLeaderboardGamesWon: boolean;
	playerLeaderboardGamesLost: boolean;
	playerLeaderboardGamesWinRatio: boolean;
	playerLeaderboardPoints: boolean;
	playerLeaderboardPointsEarned: boolean;
	playerLeaderboardPointsAgainst: boolean;
	playerLeaderboardPointsDifferential: boolean;
	playerLeaderboardTiebreakerPoints: boolean;
	playerLeaderboardTiebreakerPointsEarned: boolean;
	playerLeaderboardTiebreakerPointsAgainst: boolean;
	playerLeaderboardTiebreakerPointsDifferential: boolean;
	playerLeaderboardRatings: boolean;
	playerLeaderboardAveragePartnerRating: boolean;
	playerLeaderboardAverageOpponentRating: boolean;
	playerLeaderboardAdjustedOpponentRating: boolean;
	playerLeaderboardOverallRatings: boolean;
	playerLeaderboardLastFiveMatches: boolean;

	approvedSanctioningBodies: ApprovedSanctioningBody[];
	trackingInfo: TrackingInfo;
	teamLeagueType: TeamLeagueType;
	teamLeagueLocation: TeamLeagueLocation[];
}

export interface TeamLeagueLocation {
	uuid: string;
	venue: string;
	streetAddress: string;
	city: string;
	zip: string;
	isPrimary: boolean;
	longitude: number;
	latitude: number;
}

export enum ApproveTransfers {
	UNDEFINED = 'TRANSFER_APPROVAL_UNDEFINED',
	AUTOMATIC = 'TRANSFER_APPROVAL_AUTOMATIC',
	TRANSFER_APPROVAL_REQUIRES_COMMISSIONER = 'TRANSFER_APPROVAL_REQUIRES_COMMISSIONER',
	TRANSFER_APPROVAL_ONLY_COMMISSIONER_CAN_INITIATE = 'TRANSFER_APPROVAL_ONLY_COMMISSIONER_CAN_INITIATE'
}

export interface ApprovedSanctioningBody {
	sanctioningBodyUuid: string;
	organizationUuid: string;
	organizationName: string;
	logo: Logo | null;
}

export interface Logo {
	uuid: string;
	moduleFor: string;
	moduleUuid: string;
	url: string;
	isPrimary: boolean;
	dateCreated?: Date | null;
	dateUpdated: Date | null;
	dateDisabled: Date | null;
	createdByUuid: string;
	updatedByUuid: string | null;
}

export interface TrackingInfo {
	fbPixelIds: string;
	fbPixelDetailsPage: boolean;
	fbPixelInitiateCheckout: boolean;
	fbPixelRegistrationComplete: boolean;
	googleTagIds: string;
	googleTagDetailsPage: boolean;
	googleTagRegistrationStart: boolean;
	googleTagRegistrationComplete: boolean;
	youtubePlaylistId: string;
	youtubePlaylistApiKey: string;
	hideAfterDays: number;
}
